/**
 * SenritsuX High-Performance Audio Processing Engine
 * 
 * Provides:
 * - In-browser Web Audio API decoding for all common audio formats (MP3, WAV, FLAC, AAC, M4A, OGG).
 * - High-fidelity resampling to 44.1 kHz (44,100 Hz) via OfflineAudioContext.
 * - True 16-bit or 24-bit PCM WAV binary RIFF encoding.
 * - Technical metadata extraction (exact duration, sample rate, bit depth, channel count).
 * - Real-time progress reporting for upload & conversion UI.
 */

/**
 * Supported audio input formats and extensions
 */
export const SUPPORTED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.flac', '.aac', '.m4a', '.ogg', '.aif', '.aiff', '.wma'];
export const SUPPORTED_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/flac',
  'audio/x-flac',
  'audio/aac',
  'audio/x-aac',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
  'audio/ogg',
  'audio/aiff',
  'audio/x-aiff',
];

export const MAX_AUDIO_SIZE_BYTES = 250 * 1024 * 1024; // 250 MB max per audio file

/**
 * Validate audio file type and size before processing
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateAudioFile(file) {
  if (!file) {
    return { valid: false, error: 'No audio file selected.' };
  }

  const name = file.name.toLowerCase();
  const hasValidExt = SUPPORTED_AUDIO_EXTENSIONS.some((ext) => name.endsWith(ext));
  const hasValidMime = file.type ? SUPPORTED_AUDIO_MIME_TYPES.includes(file.type.toLowerCase()) || file.type.startsWith('audio/') : false;

  if (!hasValidExt && !hasValidMime) {
    return {
      valid: false,
      error: `Unsupported audio format: "${file.name}". Please upload MP3, WAV, FLAC, AAC, M4A, or OGG.`,
    };
  }

  if (file.size > MAX_AUDIO_SIZE_BYTES) {
    return {
      valid: false,
      error: `File size exceeds limit (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 250 MB.`,
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      error: `The file "${file.name}" is empty (0 bytes).`,
    };
  }

  return { valid: true };
}

/**
 * Format duration in seconds to MM:SS or HH:MM:SS
 * @param {number} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
  if (isNaN(seconds) || seconds < 0) return '0:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format bytes to readable size (e.g. 14.2 MB)
 * @param {number} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (isNaN(bytes) || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/**
 * Convert an AudioBuffer to a 16-bit or 24-bit PCM WAV Blob
 * @param {AudioBuffer} audioBuffer
 * @param {16 | 24} bitDepth
 * @param {(progress: number) => void} [onProgress]
 * @returns {Blob}
 */
export function audioBufferToWavBlob(audioBuffer, bitDepth = 16, onProgress) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const numSamples = audioBuffer.length;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numSamples * blockAlign;
  const bufferSize = 44 + dataSize;

  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);

  // Helper to write ASCII strings
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  /* ── RIFF Header ── */
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true); // ChunkSize
  writeString(8, 'WAVE');

  /* ── fmt Subchunk ── */
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, 1, true); // AudioFormat (1 = PCM)
  view.setUint16(22, numChannels, true); // NumChannels
  view.setUint32(24, sampleRate, true); // SampleRate
  view.setUint32(28, byteRate, true); // ByteRate
  view.setUint16(32, blockAlign, true); // BlockAlign
  view.setUint16(34, bitDepth, true); // BitsPerSample

  /* ── data Subchunk ── */
  writeString(36, 'data');
  view.setUint32(40, dataSize, true); // Subchunk2Size

  // Interleave and write channel data
  const channels = [];
  for (let c = 0; c < numChannels; c++) {
    channels.push(audioBuffer.getChannelData(c));
  }

  let offset = 44;
  const reportInterval = Math.max(1000, Math.floor(numSamples / 20));

  if (bitDepth === 16) {
    for (let i = 0; i < numSamples; i++) {
      for (let c = 0; c < numChannels; c++) {
        // Clamp sample to [-1, 1]
        let s = Math.max(-1, Math.min(1, channels[c][i]));
        // Convert to 16-bit signed integer [-32768, 32767]
        const val = s < 0 ? s * 0x8000 : s * 0x7fff;
        view.setInt16(offset, val, true);
        offset += 2;
      }
      if (onProgress && i % reportInterval === 0) {
        onProgress(Math.round((i / numSamples) * 100));
      }
    }
  } else if (bitDepth === 24) {
    for (let i = 0; i < numSamples; i++) {
      for (let c = 0; c < numChannels; c++) {
        // Clamp sample to [-1, 1]
        let s = Math.max(-1, Math.min(1, channels[c][i]));
        // Convert to 24-bit signed integer [-8388608, 8388607]
        const val = s < 0 ? s * 0x800000 : s * 0x7fffff;
        const intVal = Math.floor(val);

        // Write 3 bytes in Little-Endian
        view.setUint8(offset, intVal & 0xff);
        view.setUint8(offset + 1, (intVal >> 8) & 0xff);
        view.setUint8(offset + 2, (intVal >> 16) & 0xff);
        offset += 3;
      }
      if (onProgress && i % reportInterval === 0) {
        onProgress(Math.round((i / numSamples) * 100));
      }
    }
  }

  if (onProgress) onProgress(100);

  return new Blob([view], { type: 'audio/wav' });
}

/**
 * Process and convert an audio file to 44.1 kHz 16-bit or 24-bit PCM WAV.
 * 
 * @param {File} file - Source audio file
 * @param {16 | 24} [targetBitDepth=16] - Target bit depth
 * @param {(status: { stage: 'reading' | 'decoding' | 'resampling' | 'encoding' | 'done', progress: number, message: string }) => void} [onProgress]
 * @returns {Promise<{
 *   processedFile: File,
 *   originalFile: File,
 *   filename: string,
 *   processedFilename: string,
 *   duration: number,
 *   durationFormatted: string,
 *   sampleRate: number,
 *   bitDepth: number,
 *   channels: number,
 *   originalFormat: string,
 *   size: number,
 *   sizeFormatted: string
 * }>}
 */
export async function convertAudioToWav(file, targetBitDepth = 16, onProgress) {
  const validation = validateAudioFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const bitDepth = targetBitDepth === 24 ? 24 : 16;
  const TARGET_SAMPLE_RATE = 44100;

  // 1. Stage: Reading File
  if (onProgress) {
    onProgress({ stage: 'reading', progress: 10, message: 'Reading audio file...' });
  }

  const arrayBuffer = await file.arrayBuffer();

  // 2. Stage: Decoding
  if (onProgress) {
    onProgress({ stage: 'decoding', progress: 30, message: 'Decoding audio stream...' });
  }

  // Use AudioContext to decode
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    throw new Error('Web Audio API is not supported in this browser.');
  }

  const tempAudioCtx = new AudioContextClass();
  let decodedBuffer;
  try {
    decodedBuffer = await tempAudioCtx.decodeAudioData(arrayBuffer);
  } catch (err) {
    throw new Error(`Audio decoding failed: ${err.message || 'Corrupted or unsupported audio encoding.'}`);
  } finally {
    try {
      await tempAudioCtx.close();
    } catch {
      // Ignore cleanup error
    }
  }

  // 3. Stage: Resampling to 44.1 kHz if needed
  if (onProgress) {
    onProgress({ stage: 'resampling', progress: 60, message: 'Resampling to 44.1 kHz PCM...' });
  }

  let finalBuffer = decodedBuffer;

  if (decodedBuffer.sampleRate !== TARGET_SAMPLE_RATE) {
    const offlineCtx = new OfflineAudioContext(
      decodedBuffer.numberOfChannels,
      Math.ceil(decodedBuffer.duration * TARGET_SAMPLE_RATE),
      TARGET_SAMPLE_RATE
    );

    const source = offlineCtx.createBufferSource();
    source.buffer = decodedBuffer;
    source.connect(offlineCtx.destination);
    source.start(0);

    finalBuffer = await offlineCtx.startRendering();
  }

  // 4. Stage: Encoding to PCM WAV
  if (onProgress) {
    onProgress({ stage: 'encoding', progress: 80, message: `Encoding to ${bitDepth}-bit WAV...` });
  }

  const wavBlob = audioBufferToWavBlob(finalBuffer, bitDepth, (pct) => {
    if (onProgress) {
      onProgress({
        stage: 'encoding',
        progress: 80 + Math.floor(pct * 0.18),
        message: `Encoding to ${bitDepth}-bit WAV (${pct}%)...`,
      });
    }
  });

  const baseName = file.name.replace(/\.[^/.]+$/, '');
  const processedFilename = `${baseName}.wav`;
  const processedFile = new File([wavBlob], processedFilename, { type: 'audio/wav' });

  if (onProgress) {
    onProgress({ stage: 'done', progress: 100, message: 'Audio conversion complete.' });
  }

  return {
    processedFile,
    originalFile: file,
    filename: file.name,
    processedFilename,
    duration: finalBuffer.duration,
    durationFormatted: formatDuration(finalBuffer.duration),
    sampleRate: TARGET_SAMPLE_RATE,
    bitDepth: bitDepth,
    channels: finalBuffer.numberOfChannels,
    originalFormat: file.type || file.name.split('.').pop().toUpperCase(),
    size: wavBlob.size,
    sizeFormatted: formatFileSize(wavBlob.size),
  };
}
